import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { teacherToolsService, userDataService } from '../../../../services';
import { countWords, DEFAULT_WORD_LIMIT } from '../../../../utils/wordCount';

// `wordLimit` defaults to DEFAULT_WORD_LIMIT; pass `null` to disable the limit.
const useTextInputOrLessonSelector = ({ value, onChange, wordLimit = DEFAULT_WORD_LIMIT, multiSelect = true }) => {
  const { t } = useTranslation(['chatbot']);
  const [mode, setMode] = useState(value?.mode || 'text');
  const [textValue, setTextValue] = useState(value?.text || '');
  const [selectedLessons, setSelectedLessons] = useState(() => {
    // Initialize from lessons array or single lesson (backward compat)
    if (value?.lessons?.length > 0) return value.lessons;
    if (value?.lesson) return [value.lesson];
    return [];
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [units, setUnits] = useState([]);
  const [loadingLessonContent, setLoadingLessonContent] = useState(false);
  const [contentError, setContentError] = useState(null);

  const [tocData, setTocData] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const buildFallbackContent = (lesson) => {
    const parts = [];
    if (lesson.title) parts.push(`${t('chatbot:Lesson')}: ${lesson.title}`);
    if (lesson.unitName) parts.push(`${t('chatbot:Unit')}: ${lesson.unitName}`);
    if (lesson.courseName) parts.push(`${t('chatbot:Course')}: ${lesson.courseName}`);
    return parts.join('\n');
  };

  useEffect(() => {
    if (value) {
      setMode(value.mode || 'text');
      setTextValue(value.text || '');
      if (value.lessons?.length > 0) {
        setSelectedLessons(value.lessons);
      } else if (value.lesson) {
        setSelectedLessons([value.lesson]);
      }
    }
  }, []);

  // Fetch TOC on mount (pre-fetch so data is ready when dialog opens)
  useEffect(() => {
    let cancelled = false;

    const fetchToc = async () => {
      setLoadingCourses(true);
      try {
        const data = await userDataService.getCoursesToc();
        if (!cancelled) {
          setTocData(data);
          setCourses(data);
        }
      } catch (error) {
        console.error('[TextInputOrLessonSelector] Failed to fetch TOC:', error);
        if (!cancelled) {
          setCourses([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingCourses(false);
        }
      }
    };

    fetchToc();

    return () => {
      cancelled = true;
    };
  }, []);

  const isWithinLimit = useCallback((count) => !wordLimit || count <= wordLimit, [wordLimit]);

  const isValidText = useCallback(
    (text) => {
      const count = countWords(text);
      return count > 0 && isWithinLimit(count);
    },
    [isWithinLimit]
  );

  const wordCount = useMemo(() => countWords(textValue), [textValue]);

  const isOverLimit = !isWithinLimit(wordCount);

  const handleTextChange = useCallback(
    (text) => {
      setMode('text');
      setTextValue(text);
      setSelectedLessons([]);

      onChange({
        mode: 'text',
        text: text,
        lesson: null,
        lessons: [],
        lessonIds: [],
        contentIdsMap: {},
        isValid: isValidText(text),
      });
    },
    [onChange, isValidText]
  );

  const handleLessonsSelect = useCallback(
    async (lessons) => {
      setMode('lesson');
      setTextValue('');
      setIsDialogOpen(false);
      setContentError(null);

      // Set lessons immediately with null wordCount to indicate loading
      const lessonsWithLoading = lessons.map((l) => ({ ...l, wordCount: null }));
      setSelectedLessons(lessonsWithLoading);
      setLoadingLessonContent(true);

      // Fetch content for each lesson in parallel
      const results = await Promise.allSettled(
        lessons.map(async (lesson) => {
          const lessonId = lesson.lessonGuid || lesson.guid;
          const contentIds = lesson?.contentIds || [];
          try {
            const response = await teacherToolsService.getContentsContent(contentIds);
            let content = response?.content || response?.text || '';
            content = content.trim();
            const wc = content ? content.split(/\s+/).length : 0;
            return { ...lesson, lessonId, content, wordCount: wc };
          } catch (error) {
            console.error(`[TextInputOrLessonSelector] Failed to fetch content for lesson ${lessonId}:`, error);
            return { ...lesson, lessonId, content: '', wordCount: 0, error: 'failed' };
          }
        })
      );

      const resolvedLessons = results.map((r) => (r.status === 'fulfilled' ? r.value : { ...r.reason, content: '', wordCount: 0, error: 'failed' }));

      // Apply fallback content for lessons with empty content
      resolvedLessons.forEach((lesson, i) => {
        if (!lesson.content || lesson.content === '') {
          const fallback = buildFallbackContent(lesson);
          resolvedLessons[i] = { ...lesson, content: fallback, wordCount: fallback ? fallback.split(/\s+/).length : 0 };
        }
      });

      setSelectedLessons(resolvedLessons);
      setLoadingLessonContent(false);

      // Aggregate text from all lessons
      const aggregatedText = resolvedLessons
        .map((l) => l.content || '')
        .filter(Boolean)
        .join('\n\n');
      setTextValue(aggregatedText);

      const isValid = isValidText(aggregatedText);

      const lessonIds = resolvedLessons.map((l) => l.lessonGuid || l.guid).filter(Boolean);
      const contentIdsMap = {};
      resolvedLessons.forEach((l) => {
        const id = l.lessonGuid || l.guid;
        if (id && l.contentIds?.length) contentIdsMap[id] = l.contentIds;
      });
      onChange({
        mode: 'lesson',
        text: aggregatedText,
        lesson: resolvedLessons[0] || null,
        lessons: resolvedLessons,
        lessonIds,
        contentIdsMap,
        isValid: isValid,
      });
    },
    [onChange, isValidText]
  );

  const handleLessonSelect = useCallback(
    async (lesson) => {
      setMode('lesson');
      setTextValue('');
      setIsDialogOpen(false);
      setContentError(null);

      const lessonWithLoading = { ...lesson, wordCount: null };
      setSelectedLessons([lessonWithLoading]);
      setLoadingLessonContent(true);

      const lessonId = lesson.lessonGuid || lesson.guid;
      const contentIds = lesson?.contentIds || [];
      try {
        const response = await teacherToolsService.getContentsContent(contentIds);
        let content = response?.content || response?.text || '';
        content = content.trim();
        if (!content) {
          content = buildFallbackContent(lesson);
        }
        const wc = content ? content.split(/\s+/).length : 0;
        const resolved = { ...lesson, lessonId, content, wordCount: wc };

        setSelectedLessons([resolved]);
        setTextValue(content);

        const isValid = isValidText(content);
        const contentIdsMap = {};
        if (lessonId && lesson.contentIds?.length) contentIdsMap[lessonId] = lesson.contentIds;
        onChange({ mode: 'lesson', text: content, lesson: resolved, lessons: [resolved], lessonIds: [lessonId], contentIdsMap, isValid });
      } catch (error) {
        console.error(`[TextInputOrLessonSelector] Failed to fetch content for lesson ${lessonId}:`, error);
        setContentError('failed');
        setSelectedLessons([{ ...lesson, lessonId, content: '', wordCount: 0, error: 'failed' }]);
      } finally {
        setLoadingLessonContent(false);
      }
    },
    [onChange, isValidText]
  );

  const handleRemoveLesson = useCallback(
    (lessonGuid) => {
      if (!multiSelect) {
        // Single-select: always switch back to text mode
        setMode('text');
        setSelectedLessons([]);
        setTextValue('');
        setContentError(null);

        onChange({
          mode: 'text',
          text: '',
          lesson: null,
          lessons: [],
          lessonIds: [],
          contentIdsMap: {},
          isValid: false,
        });
        return;
      }

      const remaining = selectedLessons.filter((l) => (l.guid || l.lessonGuid) !== lessonGuid);

      if (remaining.length === 0) {
        // No lessons left - switch back to text mode
        setMode('text');
        setSelectedLessons([]);
        setTextValue('');
        setContentError(null);

        onChange({
          mode: 'text',
          text: '',
          lesson: null,
          lessons: [],
          lessonIds: [],
          contentIdsMap: {},
          isValid: false,
        });
      } else {
        setSelectedLessons(remaining);

        // Recalculate aggregated text
        const aggregatedText = remaining
          .map((l) => l.content || '')
          .filter(Boolean)
          .join('\n\n');
        setTextValue(aggregatedText);

        const isValid = isValidText(aggregatedText);

        const remainingLessonIds = remaining.map((l) => l.lessonGuid || l.guid).filter(Boolean);
        const contentIdsMap = {};
        remaining.forEach((l) => {
          const id = l.lessonGuid || l.guid;
          if (id && l.contentIds?.length) contentIdsMap[id] = l.contentIds;
        });
        onChange({
          mode: 'lesson',
          text: aggregatedText,
          lesson: remaining[0] || null,
          lessons: remaining,
          lessonIds: remainingLessonIds,
          contentIdsMap,
          isValid: isValid,
        });
      }
    },
    [selectedLessons, onChange, multiSelect, isValidText]
  );

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  // Synchronous local extraction from cached TOC data instead of async API call
  const loadCourseUnits = useCallback(
    (courseId) => {
      setLoadingUnits(true);
      setUnits([]);

      if (tocData) {
        const course = tocData.find((c) => c.id === courseId);
        const courseUnits = course?.units || [];
        setUnits(courseUnits);
      }

      setLoadingUnits(false);
    },
    [tocData]
  );

  return {
    mode,
    textValue,
    selectedLessons,
    isDialogOpen,
    wordCount,
    wordLimit,
    isOverLimit,
    loadingUnits,
    units,
    loadingLessonContent,
    contentError,
    loadingCourses,

    courses,

    handleTextChange,
    handleLessonsSelect,
    handleLessonSelect,
    handleRemoveLesson,
    openDialog,
    closeDialog,
    loadCourseUnits,
  };
};

export default useTextInputOrLessonSelector;
